import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useNowPlaying } from "../context/NowPlayingContext";
import { toast } from "sonner";
import {
  ArrowLeft, Leaf, Sun, Droplet, Wind, Thermometer, FlaskConical, Scissors, Sprout,
  AlertTriangle, Bug, BookOpen, MapPin, Ruler, Clock, TrendingUp, Heart, Sparkles, Camera,
  Music, Play, Pause, Lock, ChevronRight,
} from "lucide-react";

const PREVIEW_FADE_MS = 800;

function MetaTile({ icon: Icon, label, value, color = "#4CAF50" }) {
  if (!value) return null;
  return (
    <div
      className="rounded-xl p-3"
      style={{ background: "rgba(8,14,10,0.55)", border: `1px solid ${color}40` }}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} style={{ color }} />
        <span className="text-[10px] uppercase tracking-wider text-muted-soft">{label}</span>
      </div>
      <p className="text-xs text-soft leading-snug">{value}</p>
    </div>
  );
}

function CareSection({ icon: Icon, title, color, children, testid }) {
  return (
    <div className="care-block" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} style={{ color }} />
        <h4 className="font-semibold text-white">{title}</h4>
      </div>
      <div className="pl-6 space-y-1 text-soft">{children}</div>
    </div>
  );
}

export default function SpeciesDetailScreen() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [music, setMusic] = useState(null);
  const [error, setError] = useState(false);
  const { track: npTrack, isPlaying, previewLeft, play: playTrack } = useNowPlaying();
  const activeId = npTrack?.id;

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/species/${slug}`);
        setData(res.data);
      } catch (err) {
        console.error("[Species] detail failed:", err);
        setError(true);
        return;
      }
      try {
        const m = await api.get(`/species/${slug}/music`);
        setMusic(m.data);
      } catch (err) {
        // non-fatal — keep page usable without music block
        console.warn("[Species] music recs failed:", err);
      }
    })();
  }, [slug]);

  if (error) {
    return (
      <Shell section="search">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="glass-card-strong p-6 text-center fade-up">
          <h2 className="text-xl font-bold mb-2">Species not found</h2>
          <p className="text-soft text-sm">This species isn't in our catalogue yet.</p>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell section="search">
        <div className="glass-card-strong p-8 mt-10 text-center">
          <span className="inline-block w-3 h-3 rounded-full bg-[#4CAF50] dot-pulse" />
          <p className="text-soft text-sm mt-4">Loading species…</p>
        </div>
      </Shell>
    );
  }

  const { species, detail, care } = data;
  const isPetSafe = (detail?.pet_safety || "").toLowerCase().includes("non-toxic");

  return (
    <Shell section="search">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline"
        data-testid="species-back"
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Hero card */}
      <div
        className="glass-card-strong p-5 mb-5 fade-up"
        style={{ borderColor: "rgba(76,175,80,0.35)", boxShadow: "0 12px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset" }}
        data-testid="species-hero"
      >
        <div className="flex items-start gap-4">
          <span
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl flex-shrink-0"
            style={{ background: "rgba(76,175,80,0.18)", boxShadow: "var(--glow-green)" }}
          >
            <Leaf size={28} className="text-[#4CAF50]" strokeWidth={1.6} />
          </span>
          <div className="min-w-0 flex-1">
            <h1
              className="text-2xl font-extrabold leading-tight text-white"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.85), 0 0 18px rgba(76,175,80,0.4)" }}
              data-testid="species-common-name"
            >
              {species.common_name}
            </h1>
            <p className="text-sm italic font-medium mt-0.5" style={{ color: "#A5D6A7" }}>
              {species.scientific_name}
            </p>
            {species.family && (
              <span className="glass-pill solid mt-3 inline-flex" style={{ fontSize: 11 }}>
                {species.family}
              </span>
            )}
          </div>
        </div>

        {detail?.description && (
          <p
            className="text-sm leading-relaxed mt-4"
            data-testid="species-description"
            style={{ color: "rgba(255,255,255,0.92)" }}
          >
            {detail.description}
          </p>
        )}
      </div>

      {/* Quick facts grid */}
      {(detail?.native_to || detail?.mature_size || detail?.lifespan || detail?.growth_rate || detail?.pet_safety) && (
        <div className="grid grid-cols-2 gap-2.5 mb-5 fade-up stagger-1" data-testid="species-meta-grid">
          <MetaTile icon={MapPin} label="Native to" value={detail.native_to} color="#4CAF50" />
          <MetaTile icon={Ruler} label="Mature size" value={detail.mature_size} color="#FFD700" />
          <MetaTile icon={Clock} label="Lifespan" value={detail.lifespan} color="#4FC3F7" />
          <MetaTile icon={TrendingUp} label="Growth rate" value={detail.growth_rate} color="#FF8F00" />
          {detail.pet_safety && (
            <div
              className="col-span-2 rounded-xl p-3"
              style={{
                background: isPetSafe ? "rgba(76,175,80,0.12)" : "rgba(211,47,47,0.10)",
                border: `1px solid ${isPetSafe ? "rgba(76,175,80,0.4)" : "rgba(211,47,47,0.4)"}`,
              }}
              data-testid="species-pet-safety"
            >
              <div className="flex items-center gap-2 mb-1">
                <Heart size={13} style={{ color: isPetSafe ? "#4CAF50" : "#FF6B6B" }} />
                <span className="text-[10px] uppercase tracking-wider text-muted-soft">Pet safety</span>
              </div>
              <p className="text-sm font-medium text-soft">{detail.pet_safety}</p>
            </div>
          )}
        </div>
      )}

      {/* Fun facts */}
      {Array.isArray(detail?.fun_facts) && detail.fun_facts.length > 0 && (
        <div
          className="rounded-2xl p-5 mb-5 fade-up stagger-2"
          style={{ background: "rgba(123,31,162,0.15)", border: "1px solid rgba(212,155,255,0.30)" }}
          data-testid="species-fun-facts"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[#d49bff]" />
            <h3 className="text-base font-semibold" style={{ color: "#E1BEE7" }}>Did you know?</h3>
          </div>
          <ul className="space-y-2 text-sm text-soft">
            {detail.fun_facts.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#d49bff] mt-1.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Music recommendation for this plant */}
      {music && music.tracks && music.tracks.length > 0 && (
        <div
          className="rounded-2xl p-5 mb-5 fade-up stagger-3"
          style={{
            background: "rgba(8, 14, 10, 0.85)",
            border: "1px solid rgba(76,175,80,0.45)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.6), 0 0 18px rgba(76,175,80,0.22)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
          data-testid="species-music-recommendations"
        >
          <div className="flex items-start gap-3 mb-3">
            <span
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0"
              style={{ background: "rgba(76,175,80,0.22)", boxShadow: "var(--glow-green)" }}
            >
              <Music size={18} className="text-[#4CAF50]" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-muted-soft">Recommended music for this plant</p>
              <h3 className="text-base font-bold leading-tight mt-0.5" style={{ color: "#A5D6A7", textShadow: "0 0 14px rgba(76,175,80,0.45)" }}>
                {music.primary_benefit}
              </h3>
              <p className="text-[11px] text-soft mt-0.5">
                Tuned to <b className="text-white">{music.frequencies.join(" + ")}</b>
              </p>
            </div>
          </div>

          {music.reason && (
            <p className="text-sm leading-relaxed text-soft mb-4">{music.reason}</p>
          )}

          <div className="space-y-2" data-testid="species-music-tracks">
            {music.tracks.map((t) => {
              const isActive = activeId === t.id && isPlaying;
              const isLocked = t.access_level === "preview";
              return (
                <div
                  key={t.id}
                  className="flex items-center gap-3 p-2.5 rounded-xl"
                  style={{
                    background: isActive ? "rgba(76,175,80,0.10)" : "rgba(8,14,10,0.55)",
                    border: `1px solid ${isActive ? "rgba(76,175,80,0.5)" : "rgba(255,255,255,0.06)"}`,
                  }}
                  data-testid={`species-music-${t.id}`}
                >
                  <button
                    onClick={() => playTrack(t)}
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all hover:scale-105"
                    style={{
                      background: isActive
                        ? "linear-gradient(135deg,#4CAF50,#66BB6A)"
                        : "rgba(76,175,80,0.18)",
                      border: `1px solid ${isActive ? "rgba(76,175,80,0.7)" : "rgba(76,175,80,0.35)"}`,
                      boxShadow: isActive ? "0 0 12px rgba(76,175,80,0.5)" : "none",
                    }}
                    data-testid={`species-play-${t.id}`}
                    aria-label={isActive ? `Pause ${t.title}` : `Play ${t.title}`}
                  >
                    {isActive ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-[#4CAF50] ml-0.5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-semibold truncate text-sm">{t.title}</p>
                      {isLocked && <Lock size={10} className="text-[#FFD54F] flex-shrink-0" />}
                    </div>
                    <p className="text-[10px] text-muted-soft">
                      {t.frequency} · {t.duration}
                      {isLocked && <span className="text-[#FFD54F] ml-1">· 30s preview</span>}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={() => navigate("/music")}
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold py-2 rounded-xl transition-colors hover:bg-white/5"
            style={{ color: "#A5D6A7", border: "1px solid rgba(76,175,80,0.35)" }}
            data-testid="species-music-explore"
          >
            Explore full music library <ChevronRight size={12} />
          </button>

        </div>
      )}

      {/* Care guide (re-uses the layout from IdentifyResultScreen for consistency) */}
      {care && Object.keys(care).length > 0 && (
        <div
          className="glass-card-strong p-5 fade-up stagger-3"
          data-testid="species-care-guide"
        >
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={18} className="text-[#4CAF50]" />
            <h3 className="text-lg font-semibold">Complete Care Guide</h3>
          </div>

          <div className="space-y-4 text-sm">
            {care.watering && (
              <CareSection icon={Droplet} title="Watering" color="#4CAF50" testid="care-watering">
                {care.watering.frequency && <p><span className="text-muted-soft">Frequency:</span> {care.watering.frequency}</p>}
                {care.watering.method && <p><span className="text-muted-soft">Method:</span> {care.watering.method}</p>}
                {care.watering.signs_overwatering && <p><span className="text-[#FF8F00]">Overwatered:</span> {care.watering.signs_overwatering}</p>}
                {care.watering.signs_underwatering && <p><span className="text-[#FF8F00]">Underwatered:</span> {care.watering.signs_underwatering}</p>}
              </CareSection>
            )}
            {care.sunlight && (
              <CareSection icon={Sun} title="Light" color="#FFD700" testid="care-sunlight">
                {care.sunlight.type && <p><span className="text-muted-soft">Type:</span> {care.sunlight.type}</p>}
                {care.sunlight.hours_per_day && <p><span className="text-muted-soft">Hours/day:</span> {care.sunlight.hours_per_day}</p>}
                {care.sunlight.placement_tips && <p><span className="text-muted-soft">Placement:</span> {care.sunlight.placement_tips}</p>}
              </CareSection>
            )}
            {care.soil && (care.soil.type || care.soil.ph_range || care.soil.drainage) && (
              <CareSection icon={Sprout} title="Soil" color="#8D6E63" testid="care-soil">
                {care.soil.type && <p><span className="text-muted-soft">Type:</span> {care.soil.type}</p>}
                {care.soil.ph_range && <p><span className="text-muted-soft">pH range:</span> {care.soil.ph_range}</p>}
                {care.soil.drainage && <p><span className="text-muted-soft">Drainage:</span> {care.soil.drainage}</p>}
              </CareSection>
            )}
            {care.temperature && (care.temperature.ideal_celsius || care.temperature.min_celsius) && (
              <CareSection icon={Thermometer} title="Temperature" color="#FF8F00" testid="care-temperature">
                {care.temperature.ideal_celsius && <p><span className="text-muted-soft">Ideal:</span> {care.temperature.ideal_celsius}</p>}
                {(care.temperature.min_celsius || care.temperature.max_celsius) && (
                  <p><span className="text-muted-soft">Tolerance:</span> {care.temperature.min_celsius}{care.temperature.max_celsius ? ` – ${care.temperature.max_celsius}` : ""}</p>
                )}
              </CareSection>
            )}
            {care.humidity && (care.humidity.ideal_percentage || care.humidity.tips) && (
              <CareSection icon={Wind} title="Humidity" color="#4FC3F7" testid="care-humidity">
                {care.humidity.ideal_percentage && <p><span className="text-muted-soft">Ideal:</span> {care.humidity.ideal_percentage}</p>}
                {care.humidity.tips && <p><span className="text-muted-soft">Tips:</span> {care.humidity.tips}</p>}
              </CareSection>
            )}
            {care.fertiliser && (care.fertiliser.type || care.fertiliser.frequency) && (
              <CareSection icon={FlaskConical} title="Fertiliser" color="#9C27B0" testid="care-fertiliser">
                {care.fertiliser.type && <p><span className="text-muted-soft">Type:</span> {care.fertiliser.type}</p>}
                {care.fertiliser.frequency && <p><span className="text-muted-soft">Frequency:</span> {care.fertiliser.frequency}</p>}
                {care.fertiliser.season && <p><span className="text-muted-soft">Season:</span> {care.fertiliser.season}</p>}
              </CareSection>
            )}
            {care.pruning && (care.pruning.when || care.pruning.how) && (
              <CareSection icon={Scissors} title="Pruning" color="#4CAF50" testid="care-pruning">
                {care.pruning.when && <p><span className="text-muted-soft">When:</span> {care.pruning.when}</p>}
                {care.pruning.how && <p><span className="text-muted-soft">How:</span> {care.pruning.how}</p>}
              </CareSection>
            )}
            {care.propagation && (care.propagation.method || care.propagation.best_season) && (
              <CareSection icon={Leaf} title="Propagation" color="#4CAF50" testid="care-propagation">
                {care.propagation.method && <p><span className="text-muted-soft">Method:</span> {care.propagation.method}</p>}
                {care.propagation.best_season && <p><span className="text-muted-soft">Best season:</span> {care.propagation.best_season}</p>}
                {care.propagation.difficulty && <p><span className="text-muted-soft">Difficulty:</span> {care.propagation.difficulty}</p>}
              </CareSection>
            )}
            {Array.isArray(care.common_problems) && care.common_problems.filter(Boolean).length > 0 && (
              <CareSection icon={Bug} title="Common Problems" color="#FF8F00" testid="care-problems">
                <ul className="list-disc list-inside marker:text-[#4CAF50] space-y-1">
                  {care.common_problems.filter(Boolean).map((p) => <li key={p}>{p}</li>)}
                </ul>
              </CareSection>
            )}
            {care.toxicity && (care.toxicity.toxic_to_pets || care.toxicity.toxic_to_humans) && (
              <div
                className="care-block rounded-xl p-3"
                style={{ background: "rgba(211,47,47,0.10)", border: "1px solid rgba(211,47,47,0.35)" }}
                data-testid="care-toxicity"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <AlertTriangle size={14} className="text-[#FF6B6B]" />
                  <h4 className="font-semibold text-white">Toxicity warning</h4>
                </div>
                <div className="pl-6 space-y-1 text-soft">
                  <p>
                    {care.toxicity.toxic_to_pets && "Toxic to pets"}
                    {care.toxicity.toxic_to_pets && care.toxicity.toxic_to_humans && " · "}
                    {care.toxicity.toxic_to_humans && "Toxic to humans"}
                  </p>
                  {care.toxicity.details && <p className="text-muted-soft">{care.toxicity.details}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Identify CTA */}
      <button
        onClick={() => navigate("/identify")}
        className="w-full btn-primary mt-6 inline-flex items-center justify-center gap-2"
        data-testid="species-identify-cta"
      >
        <Camera size={16} /> Identify my plant
      </button>
    </Shell>
  );
}
